import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiFetch } from '../../services/cloudflareService';
import * as storageService from '../../services/storageService';
import { Hospital } from '../../types';
import {
    ArrowLeft, Plus, Key, Plug, Webhook, ScrollText, Copy, CheckCircle2, Trash2, RotateCw,
    Shield, Eye, EyeOff, AlertTriangle, XCircle, Loader2, Send, ToggleLeft, ToggleRight, Zap
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { AdminNavbarUser } from '../../components/AdminNavbarUser';

// ============================================================
// TYPES
// ============================================================
interface ApiKeyItem {
    id: string; hospitalId: string; label: string; permissions: string;
    isActive: number; isSandbox: number; lastUsedAt: string | null;
    requestCount: number; ipWhitelist: string | null; rateLimit: number; createdAt: string;
}
interface WebhookItem {
    id: string; hospitalId: string; url: string; events: string;
    isActive: number; lastDeliveryAt: string | null; lastDeliveryStatus: number | null;
    failureCount: number; createdAt: string;
}
interface LogItem {
    id: string; apiKeyId: string; method: string; path: string;
    statusCode: number; ipAddress: string; durationMs: number; createdAt: string;
}

type Tab = 'keys' | 'webhooks' | 'logs';

// ============================================================
// COMPONENT
// ============================================================
const AdminIntegraciones: React.FC = () => {
    const navigate = useNavigate();
    const { hospitalId: paramHospitalId } = useParams<{ hospitalId: string }>();
    const auth = storageService.getAuthData();
    const hospitalId = paramHospitalId || auth?.hospitalId || '';

    const [hospital, setHospital] = useState<Hospital | null>(null);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState<Tab>('keys');

    // API Keys
    const [keys, setKeys] = useState<ApiKeyItem[]>([]);
    const [keysLoading, setKeysLoading] = useState(false);
    const [newKeySecret, setNewKeySecret] = useState<{ id: string; secret: string } | null>(null);
    const [rotatedSecret, setRotatedSecret] = useState<{ id: string; secret: string } | null>(null);
    const [showCreateKey, setShowCreateKey] = useState(false);
    const [createForm, setCreateForm] = useState({ label: '', permissions: 'read,write', isSandbox: false, rateLimit: 100 });

    // Webhooks
    const [webhooks, setWebhooks] = useState<WebhookItem[]>([]);
    const [webhooksLoading, setWebhooksLoading] = useState(false);
    const [newWebhookSecret, setNewWebhookSecret] = useState<{ id: string; secret: string } | null>(null);
    const [showCreateWebhook, setShowCreateWebhook] = useState(false);
    const [createWebhookForm, setCreateWebhookForm] = useState({ url: '', events: 'patient.status_changed,patient.discharged' });
    const [testingWebhook, setTestingWebhook] = useState<string | null>(null);
    const [testResult, setTestResult] = useState<{ id: string; success: boolean; message: string } | null>(null);

    // Logs
    const [logs, setLogs] = useState<LogItem[]>([]);
    const [logsLoading, setLogsLoading] = useState(false);

    const [copied, setCopied] = useState('');

    const baseUrl = window.location.hostname === 'localhost' ? 'http://localhost:8787' : '';
    const primaryColor = hospital?.branding?.primaryColor || '#5B65DC';

    // ─── LOAD ──────────────────────────────────────────────
    useEffect(() => {
        (async () => {
            const h = await storageService.getHospitalById(hospitalId);
            setHospital(h || null);
            setLoading(false);
        })();
    }, [hospitalId]);

    const loadKeys = useCallback(async () => {
        setKeysLoading(true);
        try {
            const res = await apiFetch(`${baseUrl}/api/integration/keys/${hospitalId}`);
            const data = await res.json();
            setKeys(Array.isArray(data) ? data : []);
        } catch { setKeys([]); }
        setKeysLoading(false);
    }, [baseUrl, hospitalId]);

    const loadWebhooks = useCallback(async () => {
        setWebhooksLoading(true);
        try {
            const res = await apiFetch(`${baseUrl}/api/integration/webhooks/${hospitalId}`);
            const data = await res.json();
            setWebhooks(Array.isArray(data) ? data : []);
        } catch { setWebhooks([]); }
        setWebhooksLoading(false);
    }, [baseUrl, hospitalId]);

    const loadLogs = useCallback(async () => {
        setLogsLoading(true);
        try {
            const res = await apiFetch(`${baseUrl}/api/integration/keys/${hospitalId}/logs`);
            const data = await res.json();
            setLogs(Array.isArray(data) ? data : []);
        } catch { setLogs([]); }
        setLogsLoading(false);
    }, [baseUrl, hospitalId]);

    useEffect(() => {
        if (tab === 'keys') loadKeys();
        if (tab === 'webhooks') loadWebhooks();
        if (tab === 'logs') loadLogs();
    }, [tab, loadKeys, loadWebhooks, loadLogs]);

    // ─── ACTIONS ───────────────────────────────────────────
    const copyToClipboard = (text: string, id: string) => {
        navigator.clipboard.writeText(text);
        setCopied(id);
        setTimeout(() => setCopied(''), 2000);
    };

    const createKey = async () => {
        const res = await apiFetch(`${baseUrl}/api/integration/keys/${hospitalId}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(createForm),
        });
        const data = await res.json();
        if (data.success) {
            setNewKeySecret({ id: data.apiKey.id, secret: data.apiKey.secret });
            setShowCreateKey(false);
            setCreateForm({ label: '', permissions: 'read,write', isSandbox: false, rateLimit: 100 });
            loadKeys();
        }
    };

    const revokeKey = async (keyId: string) => {
        await apiFetch(`${baseUrl}/api/integration/keys/${hospitalId}/${keyId}/revoke`, { method: 'PUT' });
        loadKeys();
    };

    const rotateKey = async (keyId: string) => {
        const res = await apiFetch(`${baseUrl}/api/integration/keys/${hospitalId}/${keyId}/rotate`, { method: 'POST' });
        const data = await res.json();
        if (data.success) {
            setRotatedSecret({ id: keyId, secret: data.newSecret });
            loadKeys();
        }
    };

    const deleteKey = async (keyId: string) => {
        await apiFetch(`${baseUrl}/api/integration/keys/${hospitalId}/${keyId}`, { method: 'DELETE' });
        loadKeys();
    };

    const createWebhook = async () => {
        const events = createWebhookForm.events.split(',').map(e => e.trim());
        const res = await apiFetch(`${baseUrl}/api/integration/webhooks/${hospitalId}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ url: createWebhookForm.url, events }),
        });
        const data = await res.json();
        if (data.success) {
            setNewWebhookSecret({ id: data.webhook.id, secret: data.webhook.secret });
            setShowCreateWebhook(false);
            setCreateWebhookForm({ url: '', events: 'patient.status_changed,patient.discharged' });
            loadWebhooks();
        }
    };

    const toggleWebhook = async (wh: WebhookItem) => {
        await apiFetch(`${baseUrl}/api/integration/webhooks/${hospitalId}/${wh.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ isActive: !wh.isActive }),
        });
        loadWebhooks();
    };

    const deleteWebhook = async (whId: string) => {
        await apiFetch(`${baseUrl}/api/integration/webhooks/${hospitalId}/${whId}`, { method: 'DELETE' });
        loadWebhooks();
    };

    const testWebhook = async (whId: string) => {
        setTestingWebhook(whId);
        setTestResult(null);
        try {
            const res = await apiFetch(`${baseUrl}/api/integration/webhooks/${hospitalId}/${whId}/test`, { method: 'POST' });
            const data = await res.json();
            setTestResult({ id: whId, success: data.success, message: data.success ? `HTTP ${data.delivery.status} ${data.delivery.statusText}` : data.error });
        } catch (e: any) {
            setTestResult({ id: whId, success: false, message: e.message });
        }
        setTestingWebhook(null);
    };

    // ─── UI ────────────────────────────────────────────────
    if (loading) return null;

    const statusBadge = (code: number) => {
        if (code >= 200 && code < 300) return <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-emerald-100 text-emerald-700">{code}</span>;
        if (code >= 400 && code < 500) return <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-amber-100 text-amber-700">{code}</span>;
        return <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-red-100 text-red-700">{code}</span>;
    };

    const methodBadge = (method: string) => {
        const colors: Record<string, string> = { GET: 'bg-blue-100 text-blue-700', POST: 'bg-emerald-100 text-emerald-700', PUT: 'bg-amber-100 text-amber-700', DELETE: 'bg-red-100 text-red-700' };
        return <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full ${colors[method] || 'bg-slate-100 text-slate-700'}`}>{method}</span>;
    };

    const tabs: { id: Tab; label: string; icon: React.ElementType; count?: number }[] = [
        { id: 'keys', label: 'API Keys', icon: Key, count: keys.length },
        { id: 'webhooks', label: 'Webhooks', icon: Webhook, count: webhooks.length },
        { id: 'logs', label: 'Request Log', icon: ScrollText, count: logs.length },
    ];

    return (
        <div className="min-h-screen bg-slate-50 font-sans">
            {/* Header */}
            <nav className="bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between sticky top-0 z-40">
                <div className="flex items-center gap-3">
                    <button onClick={() => navigate(`/staff/paneladmin/${hospitalId}${window.location.search}`)} className="p-2 rounded-xl hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors">
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div className="flex items-center gap-2">
                        <div className="p-2 rounded-xl" style={{ backgroundColor: `${primaryColor}15` }}>
                            <Plug className="w-5 h-5" style={{ color: primaryColor }} />
                        </div>
                        <div>
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Integraciones</p>
                            <p className="text-sm font-bold text-slate-800">{hospital?.name || 'Hospital'}</p>
                        </div>
                    </div>
                </div>
                <AdminNavbarUser primaryColor={primaryColor} />
            </nav>

            <div className="max-w-6xl mx-auto px-6 py-8">
                {/* Top Bar: Tabs & View Docs */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
                    {/* Tabs */}
                    <div className="flex gap-2 bg-white rounded-2xl p-1.5 border border-slate-100 shadow-sm w-fit">
                        {tabs.map(t => (
                            <button
                                key={t.id}
                                onClick={() => setTab(t.id)}
                                className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all ${tab === t.id ? 'bg-slate-900 text-white shadow-md' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'}`}
                            >
                                <t.icon className="w-4 h-4" />
                                {t.label}
                                {typeof t.count === 'number' && t.count > 0 && (
                                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${tab === t.id ? 'bg-white/20' : 'bg-slate-100'}`}>{t.count}</span>
                                )}
                            </button>
                        ))}
                    </div>
                    <a href="/docs/admin_preview" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all border border-indigo-200 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 hover:shadow-sm">
                        <ScrollText className="w-4 h-4" /> Documentación API
                    </a>
                </div>

                {/* ─── SECRET REVEAL BANNERS ────────────────── */}
                <AnimatePresence>
                    {newKeySecret && (
                        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="mb-6 bg-amber-50 border-2 border-amber-300 rounded-2xl p-6">
                            <div className="flex items-start gap-3">
                                <AlertTriangle className="w-6 h-6 text-amber-600 shrink-0 mt-0.5" />
                                <div className="flex-1">
                                    <h3 className="font-bold text-amber-900 mb-1">⚠️ Guarda este Secret — No se puede recuperar</h3>
                                    <p className="text-sm text-amber-800 mb-3">Este es el único momento en que verás el secret. Cópialo ahora y guárdalo de forma segura.</p>
                                    <div className="bg-white rounded-lg p-3 flex items-center gap-3 border border-amber-200">
                                        <div className="flex-1 overflow-x-auto">
                                            <p className="text-xs text-slate-500 mb-1">Key ID:</p>
                                            <code className="text-sm font-mono text-slate-800 block">{newKeySecret.id}</code>
                                            <p className="text-xs text-slate-500 mt-2 mb-1">Secret:</p>
                                            <code className="text-sm font-mono text-slate-800 block">{newKeySecret.secret}</code>
                                        </div>
                                        <button
                                            onClick={() => copyToClipboard(`${newKeySecret.id}:${newKeySecret.secret}`, 'new-key')}
                                            className="p-2 rounded-lg bg-amber-100 hover:bg-amber-200 transition-colors shrink-0"
                                        >
                                            {copied === 'new-key' ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : <Copy className="w-5 h-5 text-amber-700" />}
                                        </button>
                                    </div>
                                    <button onClick={() => setNewKeySecret(null)} className="mt-3 text-xs font-bold text-amber-700 hover:text-amber-900">Ya lo guardé, cerrar</button>
                                </div>
                            </div>
                        </motion.div>
                    )}
                    {rotatedSecret && (
                        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="mb-6 bg-blue-50 border-2 border-blue-300 rounded-2xl p-6">
                            <div className="flex items-start gap-3">
                                <RotateCw className="w-6 h-6 text-blue-600 shrink-0 mt-0.5" />
                                <div className="flex-1">
                                    <h3 className="font-bold text-blue-900 mb-1">Nuevo Secret generado para {rotatedSecret.id}</h3>
                                    <div className="bg-white rounded-lg p-3 flex items-center gap-3 border border-blue-200 mt-2">
                                        <code className="text-sm font-mono text-slate-800 flex-1 overflow-x-auto">{rotatedSecret.secret}</code>
                                        <button onClick={() => copyToClipboard(rotatedSecret.secret, 'rotated')} className="p-2 rounded-lg bg-blue-100 hover:bg-blue-200 transition-colors shrink-0">
                                            {copied === 'rotated' ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : <Copy className="w-5 h-5 text-blue-700" />}
                                        </button>
                                    </div>
                                    <button onClick={() => setRotatedSecret(null)} className="mt-3 text-xs font-bold text-blue-700 hover:text-blue-900">Ya lo guardé, cerrar</button>
                                </div>
                            </div>
                        </motion.div>
                    )}
                    {newWebhookSecret && (
                        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="mb-6 bg-violet-50 border-2 border-violet-300 rounded-2xl p-6">
                            <div className="flex items-start gap-3">
                                <Shield className="w-6 h-6 text-violet-600 shrink-0 mt-0.5" />
                                <div className="flex-1">
                                    <h3 className="font-bold text-violet-900 mb-1">Webhook Secret — Úsalo para verificar la firma HMAC</h3>
                                    <div className="bg-white rounded-lg p-3 flex items-center gap-3 border border-violet-200 mt-2">
                                        <code className="text-sm font-mono text-slate-800 flex-1 overflow-x-auto">{newWebhookSecret.secret}</code>
                                        <button onClick={() => copyToClipboard(newWebhookSecret.secret, 'wh-secret')} className="p-2 rounded-lg bg-violet-100 hover:bg-violet-200 transition-colors shrink-0">
                                            {copied === 'wh-secret' ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : <Copy className="w-5 h-5 text-violet-700" />}
                                        </button>
                                    </div>
                                    <button onClick={() => setNewWebhookSecret(null)} className="mt-3 text-xs font-bold text-violet-700 hover:text-violet-900">Ya lo guardé, cerrar</button>
                                </div>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* ─── API KEYS TAB ─────────────────────────── */}
                {tab === 'keys' && (
                    <div>
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h2 className="text-xl font-bold text-slate-800">API Keys</h2>
                                <p className="text-sm text-slate-500">Credenciales Machine-to-Machine para sistemas como TASY, Epic, u otros HIS.</p>
                            </div>
                            <button onClick={() => setShowCreateKey(true)} className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-white shadow-md hover:shadow-lg transition-all" style={{ backgroundColor: primaryColor }}>
                                <Plus className="w-4 h-4" /> Nueva Key
                            </button>
                        </div>

                        {/* Create Key Form */}
                        <AnimatePresence>
                            {showCreateKey && (
                                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="mb-6 overflow-hidden">
                                    <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                                        <h3 className="font-bold text-slate-800 mb-4">Crear nueva API Key</h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                            <div>
                                                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Etiqueta</label>
                                                <input
                                                    value={createForm.label} onChange={e => setCreateForm(f => ({ ...f, label: e.target.value }))}
                                                    placeholder="Ej: TASY Producción"
                                                    className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none"
                                                />
                                            </div>
                                            <div>
                                                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Permisos</label>
                                                <select
                                                    value={createForm.permissions} onChange={e => setCreateForm(f => ({ ...f, permissions: e.target.value }))}
                                                    className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none bg-white"
                                                >
                                                    <option value="read,write">Lectura + Escritura</option>
                                                    <option value="read">Solo Lectura</option>
                                                    <option value="write">Solo Escritura</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Rate Limit (req/min)</label>
                                                <input
                                                    type="number" value={createForm.rateLimit} onChange={e => setCreateForm(f => ({ ...f, rateLimit: Number(e.target.value) }))}
                                                    className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none"
                                                />
                                            </div>
                                            <div className="flex items-end gap-3 pb-1">
                                                <label className="flex items-center gap-2 cursor-pointer">
                                                    <input type="checkbox" checked={createForm.isSandbox} onChange={e => setCreateForm(f => ({ ...f, isSandbox: e.target.checked }))} className="rounded" />
                                                    <span className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
                                                        <Zap className="w-4 h-4 text-amber-500" /> Modo Sandbox
                                                    </span>
                                                </label>
                                            </div>
                                        </div>
                                        <div className="flex gap-3">
                                            <button onClick={createKey} disabled={!createForm.label.trim()} className="px-5 py-2.5 rounded-xl text-sm font-bold text-white disabled:opacity-50 transition-all" style={{ backgroundColor: primaryColor }}>
                                                Crear API Key
                                            </button>
                                            <button onClick={() => setShowCreateKey(false)} className="px-5 py-2.5 rounded-xl text-sm font-bold text-slate-500 hover:bg-slate-100">
                                                Cancelar
                                            </button>
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* Keys List */}
                        {keysLoading ? (
                            <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
                        ) : keys.length === 0 ? (
                            <div className="text-center py-16 bg-white rounded-2xl border border-slate-100">
                                <Key className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                                <p className="text-slate-500 font-medium">No hay API Keys configuradas</p>
                                <p className="text-sm text-slate-400 mt-1">Crea una para conectar tu sistema HIS/EMR.</p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {keys.map(k => (
                                    <motion.div key={k.id} layout className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm hover:shadow-md transition-shadow">
                                        <div className="flex items-start justify-between gap-4">
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                                    <h3 className="font-bold text-slate-800">{k.label}</h3>
                                                    {k.isSandbox === 1 && <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-amber-100 text-amber-700">SANDBOX</span>}
                                                    {k.isActive ? (
                                                        <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-emerald-100 text-emerald-700">ACTIVA</span>
                                                    ) : (
                                                        <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-red-100 text-red-700">REVOCADA</span>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <code className="text-xs font-mono text-slate-400 bg-slate-50 px-2 py-0.5 rounded">{k.id}</code>
                                                    <button onClick={() => copyToClipboard(k.id, k.id)} className="p-1 hover:bg-slate-100 rounded transition-colors">
                                                        {copied === k.id ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5 text-slate-400" />}
                                                    </button>
                                                </div>
                                                <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                                                    <span>Permisos: <strong className="text-slate-600">{k.permissions}</strong></span>
                                                    <span>Rate: <strong className="text-slate-600">{k.rateLimit}/min</strong></span>
                                                    <span>Requests: <strong className="text-slate-600">{k.requestCount.toLocaleString()}</strong></span>
                                                    {k.lastUsedAt && <span>Último uso: <strong className="text-slate-600">{new Date(k.lastUsedAt).toLocaleDateString()}</strong></span>}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-1 shrink-0">
                                                {k.isActive === 1 && (
                                                    <>
                                                        <button onClick={() => rotateKey(k.id)} title="Rotar Secret" className="p-2 rounded-lg hover:bg-blue-50 text-slate-400 hover:text-blue-600 transition-colors">
                                                            <RotateCw className="w-4 h-4" />
                                                        </button>
                                                        <button onClick={() => revokeKey(k.id)} title="Revocar" className="p-2 rounded-lg hover:bg-amber-50 text-slate-400 hover:text-amber-600 transition-colors">
                                                            <XCircle className="w-4 h-4" />
                                                        </button>
                                                    </>
                                                )}
                                                <button onClick={() => deleteKey(k.id)} title="Eliminar permanentemente" className="p-2 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-600 transition-colors">
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* ─── WEBHOOKS TAB ─────────────────────────── */}
                {tab === 'webhooks' && (
                    <div>
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h2 className="text-xl font-bold text-slate-800">Webhooks</h2>
                                <p className="text-sm text-slate-500">Endpoints que reciben notificaciones cuando un paciente cambia de status.</p>
                            </div>
                            <button onClick={() => setShowCreateWebhook(true)} className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-white shadow-md hover:shadow-lg transition-all" style={{ backgroundColor: primaryColor }}>
                                <Plus className="w-4 h-4" /> Nuevo Webhook
                            </button>
                        </div>

                        {/* Create Webhook Form */}
                        <AnimatePresence>
                            {showCreateWebhook && (
                                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="mb-6 overflow-hidden">
                                    <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                                        <h3 className="font-bold text-slate-800 mb-4">Registrar webhook de salida</h3>
                                        <div className="space-y-4 mb-4">
                                            <div>
                                                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">URL del endpoint</label>
                                                <input
                                                    value={createWebhookForm.url} onChange={e => setCreateWebhookForm(f => ({ ...f, url: e.target.value }))}
                                                    placeholder="https://middleware.hospital.com/webhook"
                                                    className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none font-mono"
                                                />
                                            </div>
                                            <div>
                                                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1 block">Eventos (separados por coma)</label>
                                                <input
                                                    value={createWebhookForm.events} onChange={e => setCreateWebhookForm(f => ({ ...f, events: e.target.value }))}
                                                    className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 outline-none font-mono"
                                                />
                                                <p className="text-xs text-slate-400 mt-1">Eventos: patient.status_changed, patient.discharged, * (todos)</p>
                                            </div>
                                        </div>
                                        <div className="flex gap-3">
                                            <button onClick={createWebhook} disabled={!createWebhookForm.url.trim()} className="px-5 py-2.5 rounded-xl text-sm font-bold text-white disabled:opacity-50 transition-all" style={{ backgroundColor: primaryColor }}>
                                                Crear Webhook
                                            </button>
                                            <button onClick={() => setShowCreateWebhook(false)} className="px-5 py-2.5 rounded-xl text-sm font-bold text-slate-500 hover:bg-slate-100">Cancelar</button>
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* Webhooks List */}
                        {webhooksLoading ? (
                            <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
                        ) : webhooks.length === 0 ? (
                            <div className="text-center py-16 bg-white rounded-2xl border border-slate-100">
                                <Webhook className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                                <p className="text-slate-500 font-medium">No hay webhooks configurados</p>
                                <p className="text-sm text-slate-400 mt-1">Registra uno para que tu EMR reciba actualizaciones automáticas.</p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {webhooks.map(wh => {
                                    let events: string[] = [];
                                    try { events = JSON.parse(wh.events); } catch { events = [wh.events]; }
                                    const isCircuitBroken = wh.failureCount >= 5 && !wh.isActive;

                                    return (
                                        <motion.div key={wh.id} layout className={`bg-white rounded-2xl border p-5 shadow-sm transition-all ${isCircuitBroken ? 'border-red-200 bg-red-50/30' : 'border-slate-100 hover:shadow-md'}`}>
                                            <div className="flex items-start justify-between gap-4">
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                                                        <code className="text-sm font-mono text-slate-700 truncate max-w-sm">{wh.url}</code>
                                                        {wh.isActive ? (
                                                            <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-emerald-100 text-emerald-700">ACTIVO</span>
                                                        ) : isCircuitBroken ? (
                                                            <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-red-100 text-red-700 flex items-center gap-1"><AlertTriangle className="w-3 h-3"/>CIRCUIT BREAK</span>
                                                        ) : (
                                                            <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-slate-100 text-slate-500">INACTIVO</span>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                                                        {events.map(e => (
                                                            <span key={e} className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-indigo-50 text-indigo-600">{e}</span>
                                                        ))}
                                                    </div>
                                                    <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                                                        {wh.lastDeliveryAt && <span>Último envío: <strong className="text-slate-600">{new Date(wh.lastDeliveryAt).toLocaleString()}</strong></span>}
                                                        {wh.lastDeliveryStatus && <span>HTTP: {statusBadge(wh.lastDeliveryStatus)}</span>}
                                                        {wh.failureCount > 0 && <span className="text-red-500">Fallos: <strong>{wh.failureCount}/5</strong></span>}
                                                    </div>
                                                    {testResult?.id === wh.id && (
                                                        <div className={`mt-3 px-3 py-2 rounded-lg text-xs font-bold ${testResult.success ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                                                            {testResult.success ? '✅' : '❌'} {testResult.message}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-1 shrink-0">
                                                    <button onClick={() => testWebhook(wh.id)} title="Enviar test" disabled={testingWebhook === wh.id} className="p-2 rounded-lg hover:bg-indigo-50 text-slate-400 hover:text-indigo-600 transition-colors disabled:opacity-50">
                                                        {testingWebhook === wh.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                                    </button>
                                                    <button onClick={() => toggleWebhook(wh)} title={wh.isActive ? 'Desactivar' : 'Reactivar'} className="p-2 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors">
                                                        {wh.isActive ? <ToggleRight className="w-5 h-5 text-emerald-500" /> : <ToggleLeft className="w-5 h-5" />}
                                                    </button>
                                                    <button onClick={() => deleteWebhook(wh.id)} title="Eliminar" className="p-2 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-600 transition-colors">
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </div>
                                        </motion.div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                {/* ─── LOGS TAB ─────────────────────────────── */}
                {tab === 'logs' && (
                    <div>
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h2 className="text-xl font-bold text-slate-800">Request Log</h2>
                                <p className="text-sm text-slate-500">Últimas 100 peticiones de la API de integración.</p>
                            </div>
                            <button onClick={loadLogs} className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-slate-600 bg-white border border-slate-200 hover:border-slate-300 transition-colors">
                                <RotateCw className="w-4 h-4" /> Refrescar
                            </button>
                        </div>

                        {logsLoading ? (
                            <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
                        ) : logs.length === 0 ? (
                            <div className="text-center py-16 bg-white rounded-2xl border border-slate-100">
                                <ScrollText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                                <p className="text-slate-500 font-medium">Sin actividad registrada</p>
                                <p className="text-sm text-slate-400 mt-1">Los logs aparecerán aquí cuando se utilice la API.</p>
                            </div>
                        ) : (
                            <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm">
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left text-sm">
                                        <thead>
                                            <tr className="border-b border-slate-100 bg-slate-50/50">
                                                <th className="px-4 py-3 font-bold text-slate-500 text-xs uppercase tracking-wider">Hora</th>
                                                <th className="px-4 py-3 font-bold text-slate-500 text-xs uppercase tracking-wider">Método</th>
                                                <th className="px-4 py-3 font-bold text-slate-500 text-xs uppercase tracking-wider">Path</th>
                                                <th className="px-4 py-3 font-bold text-slate-500 text-xs uppercase tracking-wider">Status</th>
                                                <th className="px-4 py-3 font-bold text-slate-500 text-xs uppercase tracking-wider">Duración</th>
                                                <th className="px-4 py-3 font-bold text-slate-500 text-xs uppercase tracking-wider">IP</th>
                                                <th className="px-4 py-3 font-bold text-slate-500 text-xs uppercase tracking-wider">Key</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-50">
                                            {logs.map(log => (
                                                <tr key={log.id} className="hover:bg-slate-50/50 transition-colors">
                                                    <td className="px-4 py-2.5 text-xs text-slate-500 whitespace-nowrap">{new Date(log.createdAt).toLocaleString()}</td>
                                                    <td className="px-4 py-2.5">{methodBadge(log.method)}</td>
                                                    <td className="px-4 py-2.5 font-mono text-xs text-slate-600 max-w-[200px] truncate">{log.path}</td>
                                                    <td className="px-4 py-2.5">{statusBadge(log.statusCode)}</td>
                                                    <td className="px-4 py-2.5 text-xs text-slate-500">{log.durationMs}ms</td>
                                                    <td className="px-4 py-2.5 font-mono text-xs text-slate-400">{log.ipAddress}</td>
                                                    <td className="px-4 py-2.5 font-mono text-[10px] text-slate-400 max-w-[100px] truncate">{log.apiKeyId}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminIntegraciones;
